// Shared sync services for Roam and Checkvist
// Can be used from both Chrome extension background worker and webapp

import { convertLegacyTaskFieldsToStructuredData, ensureTodoTag } from './task-migration-helper'
import { getInboxAreaId } from './area-containers'

// Supabase REST API - Load from environment
// CRITICAL: NEVER hardcode credentials. Use environment variables.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://mhkibunsnofqrktyzcew.supabase.co'
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// SECURITY WARNING: Service role key should ONLY be used in secure backend contexts
// For Chrome extension: Use anon key + RLS + user authentication instead
// This env var should ONLY be set in secure backend environments (MCP server, scripts)
const SUPABASE_SERVICE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_KEY || ''

if (!SUPABASE_ANON_KEY) {
  console.error('[sync-services] VITE_SUPABASE_ANON_KEY not set in environment')
}

export interface RoamCredentials {
  graphName: string
  apiToken: string
}

export interface CheckvistCredentials {
  username: string
  apiKey: string
}

export interface SyncProgress {
  current: number
  total: number
}

const ROAM_API_BASE = 'https://api.roamresearch.com'
const CHECKVIST_API_BASE = 'https://checkvist.com'

// Checkvist token cache
let checkvistToken: string | null = null
let checkvistTokenExpiry: number | null = null

/**
 * Get or create workspace for user
 * Required for all node operations since workspace_id is now NOT NULL
 */
async function ensureWorkspaceId(userId: string, userToken?: string, useServiceRole?: boolean): Promise<string> {
  // Try to find existing workspace
  const workspaces = await supabaseRequest(
    `workspaces?owner_id=eq.${userId}&select=id&limit=1`,
    { userToken, useServiceRole }
  ) as Array<{ id: string }> | null

  if (workspaces && workspaces.length > 0) {
    return workspaces[0].id
  }

  // Create new workspace if none exists
  const newWorkspace = await supabaseRequest(
    'workspaces',
    {
      method: 'POST',
      prefer: 'return=representation',
      body: [{
        owner_id: userId,
        name: 'My Workspace'
      }],
      userToken,
      useServiceRole
    }
  ) as Array<{ id: string }> | null

  if (!newWorkspace || newWorkspace.length === 0) {
    throw new Error('Failed to create workspace for user: ' + userId)
  }

  console.log('[Sync] Created new workspace:', newWorkspace[0].id, 'for user:', userId)
  return newWorkspace[0].id
}

/**
 * Make authenticated request to Supabase REST API
 */
async function supabaseRequest(endpoint: string, options: any = {}) {
  const url = `${SUPABASE_URL}/rest/v1/${endpoint}`
  // Priority: userToken > service role > anon key
  // userToken is used in webapp (authenticated user)
  // service role is used in extension background (privileged context)
  let apiKey: string
  let authToken: string

  if (options.userToken) {
    // Webapp: use user's authenticated session token
    apiKey = SUPABASE_ANON_KEY
    authToken = options.userToken
  } else if (options.useServiceRole) {
    // Extension: use service role key
    apiKey = SUPABASE_SERVICE_KEY
    authToken = SUPABASE_SERVICE_KEY
  } else {
    // Fallback: anon key
    apiKey = SUPABASE_ANON_KEY
    authToken = SUPABASE_ANON_KEY
  }

  const headers: any = {
    'apikey': apiKey,
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json',
    'Prefer': options.prefer || 'return=minimal',
    ...options.headers
  }

  console.debug('[Supabase REST]', { url, method: options.method || 'GET', prefer: headers.Prefer })

  const response = await fetch(url, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: response.statusText }))
    console.error('[Supabase REST error]', {
      endpoint,
      url,
      status: response.status,
      err
    })
    throw new Error(err.message || err.error || 'Request failed')
  }

  if (response.status === 204) {
    return null
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json()
  }

  return null
}

// ===========================================
// Roam API Integration
// ===========================================

async function roamApiRequest(endpoint: string, body: any, creds: RoamCredentials) {
  const url = `${ROAM_API_BASE}/api/graph/${creds.graphName}${endpoint}`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Authorization': `Bearer ${creds.apiToken}`
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Roam API error (${response.status}): ${errorText.substring(0, 100)}`)
  }

  const data = await response.json()
  return data.result !== undefined ? data.result : data
}

function getProp(obj: any, key: string) {
  if (!obj) return undefined
  return obj[`:${key}`] ?? obj[key]
}

/**
 * Parse repeat pattern from task text
 * Returns pattern like "daily", "weekly", etc.
 */
function parseRepeatFromText(text: string): string | null {
  const match = text.match(/#repeat[\/\-](\w+)/i)
  return match ? match[1].toLowerCase() : null
}

/**
 * Parse repeat ID from task text
 * Returns ID like "#r/abc123"
 */
function parseRepeatIdFromText(text: string): string | null {
  const match = text.match(/#r\/([a-z0-9]+)/i)
  return match ? `#r/${match[1]}` : null
}

function cleanTaskName(text: string): string {
  return text
    .replace(/\{\{(\[\[)?TODO(\]\])?\}\}/gi, '')
    .replace(/\{\{(\[\[)?DONE(\]\])?\}\}/gi, '')
    .replace(/\[\[[A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?,?\s*\d{4}\]\]/gi, '')
    .replace(/(?:@|at\s+)?(\d{1,2})[:.](\d{2})\s*-\s*(\d{1,2})[:.](\d{2})(?:\s*(?:am|pm))?/gi, '')
    .replace(/(?:@|at\s+)\d{1,2}[:.](\d{2})(?:\s*(?:am|pm))?/gi, '')
    .replace(/(?:^|\s)(\d{1,2})[:.](\d{2})(?:\s*(?:am|pm))?(?=\s|$)/gi, ' ')
    .replace(/\b\d+\s*h(?:\s*\d+\s*m(?:in)?)?\b/gi, '')
    .replace(/\b\d+\s*m(?:in)?\b/gi, '')
    .replace(/\(\d+\s*h(?:\s*\d+\s*m(?:in)?)?\)/gi, '')
    .replace(/\(\d+\s*m(?:in)?\)/gi, '')
    .replace(/!+/g, '')
    .replace(/#(?:high|medium|low)?-?priority/gi, '')
    .replace(/#repeat[\/\-]\w+/gi, '')  // Remove repeat pattern
    .replace(/#r\/[a-z0-9]+/gi, '')     // Remove repeat ID
    .replace(/\[G\]\([^)]+\)/g, '')
    .replace(/\[\[([^\]]+)\]\]/g, '$1')
    .replace(/\(\([a-zA-Z0-9_-]{9}\)\)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

async function fetchRoamTasks(creds: RoamCredentials): Promise<any[]> {
  const queryString = '[:find [(pull ?b [:block/string :block/uid {:block/page [:node/title :block/uid]} {:block/parents [:block/string :block/uid]}]) ...] :where [?b :block/string ?s] (or [(clojure.string/includes? ?s "{{TODO}}")] [(clojure.string/includes? ?s "{{[[TODO]]}}")] [(clojure.string/includes? ?s "{{DONE}}")] [(clojure.string/includes? ?s "{{[[DONE]]}}")])]'

  const blocks = await roamApiRequest('/q', {
    query: queryString,
    args: []
  }, creds)

  const EXCLUDED_PAGES = ['roam/templates', 'templates']

  const tasks = []
  for (const block of (blocks || [])) {
    const text = getProp(block, 'block/string') || ''
    const uid = getProp(block, 'block/uid') || ''
    const page = getProp(block, 'block/page')
    const pageTitle = (getProp(page, 'node/title') || '').toLowerCase()

    // Skip excluded pages
    if (EXCLUDED_PAGES.some(excluded => pageTitle === excluded || pageTitle.startsWith('roam/templates'))) {
      continue
    }

    // Skip code blocks (tasks containing ```)
    if (text.includes('```')) {
      console.log('[Sync] Skipping code block task:', text.substring(0, 50))
      continue
    }

    const isDone = text.includes('{{DONE}}') || text.includes('{{[[DONE]]}}')

    tasks.push({
      id: `roam-${uid}`,
      name: cleanTaskName(text),
      note: '',
      dueDate: null,
      dueTime: null,
      duration: null,
      completed: isDone,
      priority: 0,
      tags: ['#source/roam'], // Auto-tag with source supertag
      project: getProp(page, 'node/title') || 'Unknown',
      source: 'roam',
      url: `https://roamresearch.com/#/app/${creds.graphName}/page/${uid}`,
      _blockUid: uid,
      _graphName: creds.graphName
    })
  }

  return tasks
}

// ===========================================
// Checkvist API Integration
// ===========================================

async function getCheckvistToken(creds: CheckvistCredentials): Promise<string> {
  // Check if we have a valid cached token
  if (checkvistToken && checkvistTokenExpiry && Date.now() < checkvistTokenExpiry) {
    return checkvistToken
  }

  const response = await fetch(`${CHECKVIST_API_BASE}/auth/login.json?version=2`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `username=${encodeURIComponent(creds.username)}&remote_key=${encodeURIComponent(creds.apiKey)}`
  })

  if (!response.ok) {
    throw new Error(`Checkvist authentication failed (${response.status})`)
  }

  const data = await response.json()
  checkvistToken = data.token
  // Token valid for 1 day, refresh after 12 hours
  checkvistTokenExpiry = Date.now() + (12 * 60 * 60 * 1000)

  if (!checkvistToken) {
    throw new Error('Failed to obtain Checkvist token')
  }

  return checkvistToken
}

async function checkvistApiRequest(endpoint: string, token: string, options: any = {}): Promise<any> {
  const url = new URL(endpoint, CHECKVIST_API_BASE)
  url.searchParams.set('token', token)

  const response = await fetch(url.toString(), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  })

  if (!response.ok) {
    if (response.status === 401) {
      // Token expired
      checkvistToken = null
      checkvistTokenExpiry = null
    }
    console.error(`[Checkvist] API request failed:`, {
      endpoint,
      status: response.status,
      statusText: response.statusText,
      method: options.method || 'GET'
    })
    const errorText = await response.text().catch(() => '')
    console.error('[Checkvist] Error response:', errorText)
    throw new Error(`Checkvist API error: ${response.status} - ${errorText || response.statusText}`)
  }

  return response.json()
}

function parseDueDate(content: string): string | null {
  const dateMatch = content.match(/due:(\d{4}-\d{2}-\d{2})/i)
  if (dateMatch) return dateMatch[1]
  return null
}

function parseTime(content: string): string | null {
  const timeMatch = content.match(/(?:at |@)(\d{1,2}):(\d{2})/i)
  if (timeMatch) {
    const hours = timeMatch[1].padStart(2, '0')
    const minutes = timeMatch[2]
    return `${hours}:${minutes}`
  }
  return null
}

function parsePriority(content: string): number {
  if (content.includes('!!!')) return 3
  if (content.includes('!!')) return 2
  if (content.includes('!') && !content.includes('!!')) return 1
  return 0
}

function parseDuration(content: string): number | null {
  const match = content.match(/#?(\d+)\s*d(?:ays?)?|#?(\d+)h(?:(\d+)m)?|#?(\d+)m/)
  if (!match) return null

  if (match[1]) {
    // Days (1 day = 8 hours = 480 minutes)
    return parseInt(match[1]) * 480
  } else if (match[2]) {
    // Hours + optional minutes
    let duration = parseInt(match[2]) * 60
    if (match[3]) duration += parseInt(match[3])
    return duration
  } else if (match[4]) {
    // Minutes only
    return parseInt(match[4])
  }

  return null
}

function parseASAP(content: string): boolean {
  return /due:asap/i.test(content)
}

/**
 * Extract clean note text from Checkvist notes array
 */
function extractCheckvistNotes(notes: any): string {
  if (!notes) return ''

  // Notes can be a string or an array of comment objects
  if (typeof notes === 'string') return notes

  if (Array.isArray(notes) && notes.length > 0) {
    // Extract comment text from each note object and join with newlines
    return notes
      .map(note => note.comment || '')
      .filter(comment => comment.trim().length > 0)
      .join('\n\n')
  }

  return ''
}

function cleanCheckvistContent(content: string): string {
  return content
    .replace(/\[\]/g, '') // Remove empty checkbox markers
    .replace(/due:\S+/gi, '')
    .replace(/(?:at |@)\d{1,2}:\d{2}/gi, '')
    .replace(/!{1,3}/g, '')
    .replace(/#repeat-\w+/gi, '')
    .replace(/\d+h(?:\d+m)?|\d+m/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

async function fetchCheckvistTasks(creds: CheckvistCredentials): Promise<any[]> {
  const token = await getCheckvistToken(creds)

  // Get all checklists
  const checklists = await checkvistApiRequest('/checklists.json', token)
  console.log(`[Checkvist] Found ${checklists.length} checklists`)

  const allTasks: any[] = []

  for (const checklist of checklists) {
    // Skip archived checklists
    if (checklist.archived) {
      console.log(`[Checkvist] Skipping archived checklist: ${checklist.name}`)
      continue
    }

    console.log(`[Checkvist] Fetching tasks from checklist: ${checklist.name} (id: ${checklist.id})`)

    try {
      // Fetch tasks with notes and closed tasks included
      // with_closed=true is needed to get completed tasks when using with_notes=true
      const tasks = await checkvistApiRequest(`/checklists/${checklist.id}/tasks.json?with_notes=true&with_closed=true`, token)
      console.log(`[Checkvist] Got ${tasks.length} raw tasks from ${checklist.name}`)

      // Transform tasks recursively
      const processTask = (task: any, parentPath: string[] = []) => {
        // Check if task is ASAP (from task.due field or content)
        const isASAP = parseASAP(task.content) || (task.due && task.due.toUpperCase() === 'ASAP')

        // Use task.due field from API, fallback to parsing content
        // API returns "YYYY/MM/DD", convert to "YYYY-MM-DD"
        // For ASAP tasks, set dueDate to null (ASAP is stored in separate isASAP field)
        let dueDate: string | null = null
        if (task.due && task.due.toUpperCase() !== 'ASAP') {
          dueDate = task.due.replace(/\//g, '-')
        } else if (!task.due) {
          dueDate = parseDueDate(task.content)
        }

        const dueTime = parseTime(task.content)
        const priority = task.priority || parsePriority(task.content)
        const rawDuration = parseDuration(task.content)
        // Validate duration - max 1 week (10080 minutes)
        const duration = rawDuration && rawDuration > 0 && rawDuration < 10080 ? rawDuration : null

        // Parse repeat metadata BEFORE cleaning
        const repeatPattern = parseRepeatFromText(task.content)
        const repeatId = parseRepeatIdFromText(task.content)

        const cleanName = cleanCheckvistContent(task.content)

        // Parse tags
        const tags: string[] = ['#source/checkvist'] // Auto-tag with source supertag
        let agileStatus: string | null = null
        if (task.tags) {
          Object.keys(task.tags).forEach(tag => {
            tags.push(tag)
            const lowerTag = tag.toLowerCase()
            if (lowerTag === 'now' || lowerTag === 'next' || lowerTag === 'later') {
              agileStatus = lowerTag
            }
          })
        }

        // ASAP is treated as agile status "now"
        if (isASAP && !agileStatus) {
          agileStatus = 'now'
        }

        // FILTER: Only import OPEN tasks that have (due date OR agile tag OR ASAP)
        const isOpen = task.status !== 1
        const hasDueDate = dueDate !== null
        const hasAgileTag = agileStatus !== null

        if (isOpen && (hasDueDate || hasAgileTag || isASAP)) {
          const url = `https://checkvist.com/checklists/${checklist.id}#task_${task.id}`

          // Extract clean notes text
          const cleanNotes = extractCheckvistNotes(task.notes)

          // Debug: Log if this is Test123
          if (cleanName.toLowerCase().includes('test123')) {
            console.log('[Checkvist] Test123 task data:', {
              id: task.id,
              content: task.content,
              rawNotes: task.notes,
              cleanNotes: cleanNotes,
              hasNotes: !!cleanNotes
            })
          }

          allTasks.push({
            id: `cv-${checklist.id}-${task.id}`,
            name: cleanName,
            note: cleanNotes,
            dueDate: dueDate,
            dueTime: dueTime,
            duration: duration,
            completed: task.status === 1,
            priority: priority,
            tags: tags,
            agileStatus: agileStatus,
            isASAP: isASAP,
            repeat: repeatPattern,
            repeatId: repeatId,
            project: checklist.name,
            projectPath: parentPath.join(' > '),
            source: 'checkvist',
            url: url,
            _checklistId: checklist.id,
            _taskId: task.id
          })
        }

        // Process children recursively
        if (task.children && task.children.length > 0) {
          const newPath = [...parentPath, cleanName]
          task.children.forEach((child: any) => processTask(child, newPath))
        }
      }

      const beforeCount = allTasks.length
      tasks.forEach((task: any) => processTask(task, []))
      const afterCount = allTasks.length
      console.log(`[Checkvist] Filtered to ${afterCount - beforeCount} tasks from ${checklist.name} (${tasks.length} raw -> ${afterCount - beforeCount} after filtering)`)

    } catch (error) {
      console.error(`[Checkvist] Error fetching tasks from ${checklist.name}:`, error)
    }
  }

  console.log(`[Checkvist] Total tasks after filtering: ${allTasks.length}`)
  return allTasks
}

// ===========================================
// Public Sync Functions
// ===========================================

export async function syncRoamToSupabase(
  userId: string,
  credentials: RoamCredentials,
  onProgress?: (progress: SyncProgress) => void,
  userToken?: string
) {
  console.log('[Sync] Starting Roam sync for user:', userId);

  try {
    // CRITICAL: Get workspace_id before creating any nodes
    const workspaceId = await ensureWorkspaceId(userId, userToken, !userToken)
    console.log('[Sync] Using workspace:', workspaceId)

    // Get or create Inbox area (all synced nodes go to Inbox by default)
    const inboxId = await getInboxAreaId(userId)
    console.log('[Sync] Using Inbox area:', inboxId)

    // Fetch tasks from Roam
    console.log('[Sync] Fetching tasks from Roam...');
    const roamTasks = await fetchRoamTasks(credentials)
    console.log('[Sync] Fetched', roamTasks.length, 'tasks from Roam');

    if (roamTasks.length === 0) {
      return { success: true, synced: 0, message: 'No tasks found in Roam' };
    }

    // TEMP: Skip folder creation to match old working code
    /*
    // Step 1: Create folder structure
    // Root folder for Roam
    const rootFolderId = `${userId}_folder_roam`
    const rootFolder = {
      id: rootFolderId,
      user_id: userId,
      type: 'folder',
      content: 'Imported from Roam',
      parent_id: null, // Root folder has no parent
      position: 0,
      source: 'roam',
      metadata: { graphName: credentials.graphName, isRoot: true },
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    // Create folders for each parent page (project)
    // Use a Map to ensure unique folder IDs (handle duplicate normalized names)
    const projects = [...new Set(roamTasks.map(t => t.project))]
    const projectIdMap = new Map<string, string>() // Maps original project name to unique folder ID
    const usedIds = new Set<string>()

    projects.forEach(project => {
      // Use underscores instead of hyphens for ltree compatibility
      let baseId = project.toLowerCase().replace(/[^a-z0-9]+/g, '_')
      let uniqueId = baseId
      let counter = 1

      // Ensure uniqueness by adding counter if needed
      while (usedIds.has(uniqueId)) {
        uniqueId = `${baseId}_${counter}`
        counter++
      }

      usedIds.add(uniqueId)
      projectIdMap.set(project, uniqueId)
    })

    const projectFolders = projects.map((project, index) => {
      const projectId = projectIdMap.get(project)!
      return {
        id: `${userId}_folder_roam_${projectId}`,
        user_id: userId,
        type: 'folder',
        content: project,
        parent_id: rootFolderId,
        position: index,
        source: 'roam',
        metadata: { graphName: credentials.graphName, projectName: project },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    })

    // Upsert folders in correct order: root first, then children
    console.log('[Sync] Creating Roam root folder')
    await supabaseRequest('nodes', {
      method: 'POST',
      body: [{
        id: rootFolder.id,
        user_id: rootFolder.user_id,
        type: rootFolder.type,
        content: rootFolder.content,
        parent_id: null,
        position: 0,
        source: rootFolder.source,
        metadata: rootFolder.metadata,
        created_at: rootFolder.created_at,
        updated_at: rootFolder.updated_at
      }],
      prefer: 'resolution=merge-duplicates,return=minimal',
      userToken: userToken,
      useServiceRole: !userToken
    })

    console.log('[Sync] Creating', projectFolders.length, 'project folders')
    await supabaseRequest('nodes', {
      method: 'POST',
      body: projectFolders.map(f => ({
        id: f.id,
        user_id: f.user_id,
        type: f.type,
        content: f.content,
        parent_id: f.parent_id,
        position: f.position,
        source: f.source,
        metadata: f.metadata,
        created_at: f.created_at,
        updated_at: f.updated_at
      })),
      prefer: 'resolution=merge-duplicates,return=minimal',
      userToken: userToken,
      useServiceRole: !userToken
    })
    */

    // Step 2: Transform tasks (flat structure like old code)
    // Deduplicate by ID first (same task might appear in hierarchy multiple times)
    const uniqueTasks = new Map<string, any>()
    roamTasks.forEach(task => {
      if (!uniqueTasks.has(task.id)) {
        uniqueTasks.set(task.id, task)
      }
    })

    const supabaseTasks = Array.from(uniqueTasks.values()).map(task => ({
      id: task.id, // Keep original roam-{blockUid} format
      user_id: userId,
      name: task.name,
      note: task.note || '',
      dueDate: task.dueDate || null,
      dueTime: task.dueTime || null,
      duration: task.duration || null,
      completed: task.completed || false,
      status: task.completed ? 'done' : 'todo',
      priority: task.priority || 0,
      tags: task.tags || [],
      agileStatus: null, // Roam doesn't have agile status
      repeat: task.repeat || null,
      repeatId: task.repeatId || null,
      source: 'roam',
      url: task.url || null,
      sourceUrl: task.url || null,
      // NO parent_id, NO position - flat structure like old code
      updated_at: new Date().toISOString()
    }))

    console.log(`[Sync] Deduplicated ${roamTasks.length} tasks to ${supabaseTasks.length} unique tasks`)

    // Step 3: Batch upsert tasks to Supabase using nodes table
    // Using proper upsert with resolution=merge-duplicates to prevent duplicates
    // In webapp: uses userToken (authenticated user)
    // In extension: uses service role key (privileged context)

    console.log('[Sync] Upserting', supabaseTasks.length, 'tasks (flat structure with supertags)')
    await supabaseRequest('nodes', {
      method: 'POST',
      prefer: 'resolution=merge-duplicates,return=minimal',
      body: supabaseTasks.map(t => {
        // Convert legacy fields to structured_data for #todo supertag
        const structuredData = convertLegacyTaskFieldsToStructuredData({
          priority: t.priority,
          note: t.note,
          due_date: t.dueDate,
          due_time: t.dueTime,
          duration: t.duration,
          agile_status: t.agileStatus,
          completed: t.completed,
          status: t.status,
          tags: t.tags
        })

        return {
          id: t.id,
          user_id: t.user_id,
          workspace_id: workspaceId, // REQUIRED: workspace_id is NOT NULL
          type: 'task', // All synced items are tasks
          content: t.name, // nodes table uses 'content' not 'name'
          note: t.note,
          due_date: t.dueDate, // snake_case for nodes table
          due_time: t.dueTime,
          duration: t.duration,
          completed: t.completed,
          status: t.status,
          priority: t.priority,
          tags: ensureTodoTag(t.tags), // Ensure 'todo' tag is included
          agile_status: t.agileStatus, // snake_case for nodes table
          repeat: t.repeat, // repeat pattern (daily, weekly, etc)
          repeat_id: t.repeatId, // repeat ID (#r/abc123)
          source: t.source,
          url: t.url,
          source_url: t.sourceUrl, // snake_case for nodes table
          structured_data: structuredData, // NEW: Supertag structured data
          parent_id: inboxId, // Organize under Inbox area (source preserved as metadata)
          updated_at: t.updated_at
        }
      }),
      userToken: userToken,
      useServiceRole: !userToken // Only use service role if no user token provided
    });

    console.log('[Sync] Sync complete:', supabaseTasks.length, 'tasks synced in hierarchical folder');

    // Send final progress update
    if (onProgress) {
      onProgress({ current: roamTasks.length, total: roamTasks.length });
    }

    return { success: true, synced: supabaseTasks.length, total: roamTasks.length };

  } catch (error: any) {
    console.error('[Sync] Roam sync failed:', error);
    throw error;
  }
}

export async function syncCheckvistToSupabase(
  userId: string,
  credentials: CheckvistCredentials,
  onProgress?: (progress: SyncProgress) => void,
  userToken?: string
) {
  console.log('[Sync] Starting Checkvist 2-way sync for user:', userId);

  try {
    // CRITICAL: Get workspace_id before creating any nodes
    const workspaceId = await ensureWorkspaceId(userId, userToken, !userToken)
    console.log('[Sync] Using workspace:', workspaceId)

    // Get or create Inbox area (all synced nodes go to Inbox by default)
    const inboxId = await getInboxAreaId(userId)
    console.log('[Sync] Using Inbox area:', inboxId)

    // PHASE 1: Push local changes back to Checkvist (2-way sync)
    console.log('[Sync] Phase 1: Pushing local changes to Checkvist...')
    const localNodes = await supabaseRequest(
      `nodes?user_id=eq.${userId}&source=eq.checkvist&select=id,due_date,due_time,updated_at`,
      { userToken, useServiceRole: !userToken }
    ) as Array<{ id: string; due_date?: string | null; due_time?: string | null; updated_at: string }> | null

    // Fetch current Checkvist tasks to compare
    const currentCheckvistTasks = await fetchCheckvistTasks(credentials)
    const checkvistTasksMap = new Map(currentCheckvistTasks.map(t => [t.id, t]))

    let pushedCount = 0
    const pushedTaskIds = new Set<string>() // Track tasks we pushed changes for
    if (localNodes && localNodes.length > 0) {
      for (const localNode of localNodes) {
        const checkvistTask = checkvistTasksMap.get(localNode.id)
        if (!checkvistTask) continue

        // Check if local due_date is different from Checkvist's
        const localDate = localNode.due_date || null
        const checkvistDate = checkvistTask.dueDate || null

        if (localDate !== checkvistDate) {
          console.log(`[Sync] Pushing date change to Checkvist: ${localNode.id} (${checkvistDate} → ${localDate})`)
          try {
            await updateCheckvistTaskDate(localNode.id, localDate, credentials)
            pushedTaskIds.add(localNode.id) // Remember we pushed this task
            pushedCount++
          } catch (error) {
            console.error(`[Sync] Failed to push date change for ${localNode.id}:`, error)
          }
        }
      }
    }
    console.log(`[Sync] Pushed ${pushedCount} local changes to Checkvist`)

    // PHASE 2: Fetch tasks from Checkvist (refresh after pushing changes)
    console.log('[Sync] Phase 2: Fetching tasks from Checkvist...');
    const checkvistTasks = await fetchCheckvistTasks(credentials)
    console.log('[Sync] Fetched', checkvistTasks.length, 'tasks from Checkvist');

    if (checkvistTasks.length === 0) {
      return { success: true, synced: 0, message: 'No tasks found in Checkvist' };
    }

    // TEMP: Skip folder creation to match old working code
    /*
    // Step 1: Create folder structure
    // Root folder for Checkvist
    const rootFolderId = `${userId}_folder_checkvist`
    const rootFolder = {
      id: rootFolderId,
      user_id: userId,
      type: 'folder',
      content: 'Checkvist',
      parent_id: null, // Root folder has no parent
      position: 0,
      source: 'checkvist',
      metadata: { isRoot: true }, // All folders must have metadata field
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    // Create folders for each checklist
    const checklistIds = [...new Set(checkvistTasks.map(t => t._checklistId))]
    const checklistFolders = checklistIds.map((checklistId, index) => {
      const checklistName = checkvistTasks.find(t => t._checklistId === checklistId)?.project || 'Unknown'
      return {
        id: `${userId}_folder_cv_${checklistId}`,
        user_id: userId,
        type: 'folder',
        content: checklistName,
        parent_id: rootFolderId,
        position: index,
        source: 'checkvist',
        metadata: { checklistId },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    })

    // Upsert folders in correct order: root first, then children
    console.log('[Sync] Creating root folder')
    await supabaseRequest('nodes', {
      method: 'POST',
      body: [{
        id: rootFolder.id,
        user_id: rootFolder.user_id,
        type: rootFolder.type,
        content: rootFolder.content,
        parent_id: null,
        position: 0,
        source: rootFolder.source,
        metadata: rootFolder.metadata,
        created_at: rootFolder.created_at,
        updated_at: rootFolder.updated_at
      }],
      prefer: 'resolution=merge-duplicates,return=minimal',
      userToken: userToken,
      useServiceRole: !userToken
    })

    console.log('[Sync] Creating', checklistFolders.length, 'checklist folders')
    await supabaseRequest('nodes', {
      method: 'POST',
      prefer: 'resolution=merge-duplicates,return=minimal',
      body: checklistFolders.map(f => ({
        id: f.id,
        user_id: f.user_id,
        type: f.type,
        content: f.content,
        parent_id: f.parent_id,
        position: f.position,
        source: f.source,
        metadata: f.metadata,
        created_at: f.created_at,
        updated_at: f.updated_at
      })),
      userToken: userToken,
      useServiceRole: !userToken
    })
    */

    // Step 2: Transform tasks (flat structure like old code)
    // Deduplicate by ID first (same task might appear in hierarchy multiple times)
    // IMPORTANT: Skip tasks we just pushed to avoid overwriting with stale API data
    // (Checkvist API might not have updated yet when we fetch in Phase 2)
    const tasksToSync = checkvistTasks.filter(task => !pushedTaskIds.has(task.id))

    if (pushedTaskIds.size > 0) {
      console.log(`[Sync] Skipping ${pushedTaskIds.size} tasks we just pushed to avoid overwrite`)
    }

    const uniqueTasks = new Map<string, any>()
    tasksToSync.forEach(task => {
      if (!uniqueTasks.has(task.id)) {
        uniqueTasks.set(task.id, task)
      }
    })

    const supabaseTasks = Array.from(uniqueTasks.values()).map(task => ({
      id: task.id, // Keep original cv-{checklistId}-{taskId} format
      user_id: userId,
      name: task.name,
      note: task.note || '',
      dueDate: task.dueDate || null,
      dueTime: task.dueTime || null,
      duration: task.duration || null,
      completed: task.completed || false,
      status: task.completed ? 'done' : 'todo',
      priority: task.priority || 0,
      tags: task.tags || [],
      agileStatus: task.agileStatus || null,
      repeat: task.repeat || null,
      repeatId: task.repeatId || null,
      source: 'checkvist',
      url: task.url || null,
      sourceUrl: task.url || null,
      // NO parent_id, NO position - flat structure like old code
      updated_at: new Date().toISOString()
    }))

    console.log(`[Sync] Filtered ${tasksToSync.length} tasks (from ${checkvistTasks.length} total) to ${supabaseTasks.length} unique tasks`)

    // Step 3: Batch upsert tasks to Supabase using nodes table
    // Using proper upsert with resolution=merge-duplicates to prevent duplicates
    // In webapp: uses userToken (authenticated user)
    // In extension: uses service role key (privileged context)

    console.log('[Sync] Upserting', supabaseTasks.length, 'tasks (flat structure with supertags)')
    await supabaseRequest('nodes', {
      method: 'POST',
      prefer: 'resolution=merge-duplicates,return=minimal',
      body: supabaseTasks.map(t => {
        // Convert legacy fields to structured_data for #todo supertag
        const structuredData = convertLegacyTaskFieldsToStructuredData({
          priority: t.priority,
          note: t.note,
          due_date: t.dueDate,
          due_time: t.dueTime,
          duration: t.duration,
          agile_status: t.agileStatus,
          completed: t.completed,
          status: t.status,
          tags: t.tags
        })

        return {
          id: t.id,
          user_id: t.user_id,
          workspace_id: workspaceId, // REQUIRED: workspace_id is NOT NULL
          type: 'task', // All synced items are tasks
          content: t.name, // nodes table uses 'content' not 'name'
          note: t.note,
          due_date: t.dueDate, // snake_case for nodes table
          due_time: t.dueTime,
          duration: t.duration,
          completed: t.completed,
          status: t.status,
          priority: t.priority,
          tags: ensureTodoTag(t.tags), // Ensure 'todo' tag is included
          agile_status: t.agileStatus, // snake_case for nodes table
          repeat: t.repeat, // repeat pattern (daily, weekly, etc)
          repeat_id: t.repeatId, // repeat ID (#r/abc123)
          source: t.source,
          url: t.url,
          source_url: t.sourceUrl, // snake_case for nodes table
          structured_data: structuredData, // NEW: Supertag structured data
          parent_id: inboxId, // Organize under Inbox area (source preserved as metadata)
          updated_at: t.updated_at
        }
      }),
      userToken: userToken,
      useServiceRole: !userToken // Only use service role if no user token provided
    });

    console.log(`[Sync] 2-way sync complete: Pushed ${pushedCount} changes, synced ${supabaseTasks.length} tasks from Checkvist`);

    // Send final progress update
    if (onProgress) {
      onProgress({ current: checkvistTasks.length, total: checkvistTasks.length });
    }

    return {
      success: true,
      synced: supabaseTasks.length,
      total: checkvistTasks.length,
      pushed: pushedCount
    };

  } catch (error: any) {
    console.error('[Sync] Checkvist sync failed:', error);
    throw error;
  }
}

// ===========================================
// 2-Way Sync: Update tasks back to source
// ===========================================

/**
 * Find task in nested tree structure
 */
function findTaskById(tasks: any[], targetId: number): any | null {
  for (const task of tasks) {
    if (task.id === targetId) {
      return task
    }
    if (task.children && task.children.length > 0) {
      const found = findTaskById(task.children, targetId)
      if (found) return found
    }
  }
  return null
}

/**
 * Update Checkvist task date
 */
export async function updateCheckvistTaskDate(
  taskId: string,
  newDate: string | null,
  credentials: CheckvistCredentials
): Promise<boolean> {
  try {
    // Parse ID format: cv-{checklistId}-{taskId}
    if (!taskId.startsWith('cv-')) {
      throw new Error('Invalid Checkvist task ID format')
    }
    const parts = taskId.substring(3).split('-') // Remove 'cv-' and split remaining
    if (parts.length < 2) {
      throw new Error('Invalid Checkvist task ID format')
    }
    const checklistId = parts[0]
    const cvTaskId = parts[1]

    const token = await getCheckvistToken(credentials)

    // Get current task to preserve content
    const tasks = await checkvistApiRequest(`/checklists/${checklistId}/tasks.json`, token)
    const task = findTaskById(tasks, parseInt(cvTaskId))

    if (!task) {
      console.error('[Checkvist] Task not found:', taskId)
      return false
    }

    // Update content with new date
    let newContent = task.content.replace(/due:\S+/gi, '').trim()
    if (newDate) {
      newContent = `${newContent} due:${newDate}`
    }

    await checkvistApiRequest(
      `/checklists/${checklistId}/tasks/${cvTaskId}.json`,
      token,
      {
        method: 'PUT',
        body: JSON.stringify({ 'task[content]': newContent })
      }
    )

    console.log('[Checkvist] Updated task date:', taskId, newDate)
    return true
  } catch (error) {
    console.error('[Checkvist] Error updating date:', error)
    return false
  }
}

/**
 * Toggle Checkvist task completion
 */
export async function toggleCheckvistTaskComplete(
  taskId: string,
  completed: boolean,
  credentials: CheckvistCredentials
): Promise<boolean> {
  try {
    // Parse ID format: cv-{checklistId}-{taskId}
    const parts = taskId.split('-')
    if (parts.length !== 3 || parts[0] !== 'cv') {
      throw new Error(`Invalid Checkvist task ID format: ${taskId}`)
    }

    const checklistId = parts[1]
    const cvTaskId = parts[2]

    const token = await getCheckvistToken(credentials)

    const endpoint = completed
      ? `/checklists/${checklistId}/tasks/${cvTaskId}/close.json`
      : `/checklists/${checklistId}/tasks/${cvTaskId}/reopen.json`

    await checkvistApiRequest(endpoint, token, { method: 'POST' })

    console.log('[Checkvist] Toggled task completion:', taskId, completed)
    return true
  } catch (error) {
    console.error('[Checkvist] Error toggling completion:', error)
    return false
  }
}

/**
 * Update Checkvist task agile status (now/next/later tags)
 */
export async function updateCheckvistAgileStatus(
  taskId: string,
  status: string | null,
  credentials: CheckvistCredentials
): Promise<boolean> {
  try {
    // Parse ID format: cv-{checklistId}-{taskId}
    const parts = taskId.split('-')
    if (parts.length !== 3 || parts[0] !== 'cv') {
      throw new Error(`Invalid Checkvist task ID format: ${taskId}`)
    }

    const checklistId = parts[1]
    const cvTaskId = parts[2]

    const token = await getCheckvistToken(credentials)

    // Get current task to get existing tags
    const tasks = await checkvistApiRequest(`/checklists/${checklistId}/tasks.json`, token)
    const task = findTaskById(tasks, parseInt(cvTaskId))

    if (!task) {
      console.error('[Checkvist] Task not found:', taskId)
      return false
    }

    // Get current tags, remove agile tags
    const currentTags = task.tags ? Object.keys(task.tags) : []
    const agileTags = ['now', 'next', 'later']
    const nonAgileTags = currentTags.filter(tag =>
      !agileTags.includes(tag.toLowerCase())
    )

    // Add new agile status if provided
    const newTags = status ? [...nonAgileTags, status] : nonAgileTags
    const tagString = newTags.join(',')

    await checkvistApiRequest(
      `/checklists/${checklistId}/tasks/${cvTaskId}.json`,
      token,
      {
        method: 'PUT',
        body: JSON.stringify({ 'task[tags]': tagString })
      }
    )

    console.log('[Checkvist] Updated agile status:', taskId, status)
    return true
  } catch (error) {
    console.error('[Checkvist] Error updating agile status:', error)
    return false
  }
}

/**
 * Update Roam block date
 */
export async function updateRoamTaskDate(
  taskId: string,
  newDate: string | null,
  credentials: RoamCredentials
): Promise<boolean> {
  try {
    // Extract blockUid from format: roam-{blockUid}
    if (!taskId.startsWith('roam-')) {
      throw new Error('Invalid Roam task ID format')
    }
    const blockUid = taskId.substring(5) // Remove 'roam-' prefix

    // Get current block content
    const query = `[:find (pull ?b [:block/string]) :where [?b :block/uid "${blockUid}"]]`
    const result = await roamApiRequest('/q', { query }, credentials)

    if (!result || result.length === 0) {
      console.error('[Roam] Block not found:', blockUid)
      return false
    }

    let content = result[0][0]['block/string']

    // Remove old due date if present
    content = content.replace(/{{(?:\[\[)?date:\s*\[\[([^\]]+)\]\](?:\]\])?}}/g, '')

    // Add new due date if provided
    if (newDate) {
      // Convert YYYY-MM-DD to Roam format: {{[[date: [[January 15th, 2024]]]]}}
      const dateObj = new Date(newDate)
      const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
      const roamDate = formatter.format(dateObj).replace(/(\d+)/, '$1th') // Simple ordinal
      content = `${content} {{[[date: [[${roamDate}]]]]}}`
    }

    // Update block
    await roamApiRequest(
      '/block/update',
      {
        block: {
          uid: blockUid,
          string: content.trim()
        }
      },
      credentials
    )

    console.log('[Roam] Updated block date:', blockUid, newDate)
    return true
  } catch (error) {
    console.error('[Roam] Error updating date:', error)
    return false
  }
}

/**
 * Toggle Roam task completion
 */
export async function toggleRoamTaskComplete(
  taskId: string,
  completed: boolean,
  credentials: RoamCredentials
): Promise<boolean> {
  try {
    // Extract blockUid from format: roam-{blockUid}
    if (!taskId.startsWith('roam-')) {
      throw new Error('Invalid Roam task ID format')
    }
    const blockUid = taskId.substring(5) // Remove 'roam-' prefix

    // Get current block content
    const query = `[:find (pull ?b [:block/string :block/heading]) :where [?b :block/uid "${blockUid}"]]`
    const result = await roamApiRequest('/q', { query }, credentials)

    if (!result || result.length === 0) {
      console.error('[Roam] Block not found:', blockUid)
      return false
    }

    let content = result[0][0]['block/string']
    const heading = result[0][0]['block/heading'] || 0

    // Toggle TODO/DONE
    if (completed) {
      // Mark as DONE
      content = content.replace(/{{(?:TODO|DONE)}}/, '{{DONE}}')
      if (!content.includes('{{DONE}}')) {
        content = '{{DONE}} ' + content
      }
    } else {
      // Mark as TODO
      content = content.replace(/{{DONE}}/, '{{TODO}}')
      if (!content.includes('{{TODO}}') && heading === 3) {
        content = '{{TODO}} ' + content
      }
    }

    // Update block
    await roamApiRequest(
      '/block/update',
      {
        block: {
          uid: blockUid,
          string: content.trim(),
          heading: heading
        }
      },
      credentials
    )

    console.log('[Roam] Toggled task completion:', blockUid, completed)
    return true
  } catch (error) {
    console.error('[Roam] Error toggling completion:', error)
    return false
  }
}
